import { createStyles, makeStyles, Theme, } from '@material-ui/core';
import { Button, Menu, MenuItem, Stack, TextField, Tooltip, Snackbar, Alert } from '@mui/material';
import React from 'react';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { connect } from 'react-redux';
import { useLocalStorage } from 'usehooks-ts';
import { GridValidRowModel } from '@mui/x-data-grid';
import { LateSwapLineup, LateSwapPlayer } from './store/types';
import { UpgradeModal } from '../common/LockIcon';
import { findLineupsToFavorite } from './utils';
import { SlateResponse } from '../common/store/types';
import { StackExposures } from '../preContest/queries/queryTypes';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const useStyles = makeStyles((theme: Theme) =>createStyles({
  quickFavorite: {
    '& .MuiButton-root': {
      color: 'white',
      border: '1px solid #DB4D00',
      padding: '4px 8px',
    },
    '& .MuiListItem-root.Mui-disabled': {
      display: 'flex',
      justifyContent: 'space-between',
    }
  },
  disabled: {
    color: "#8F9296",
    cursor: 'not-allowed',
    '& svg': {
      color: "#8F9296",
    }
  },
})
);

interface OwnProps {
  readonly lineups: LateSwapLineup[];
  readonly players: LateSwapPlayer[];
  readonly disabled: boolean;
  readonly contestName: string;
  readonly boostedPlayers: { [key: string]: number };
  readonly boostedStacks: { [key: string]: number };
  readonly playerExposureDict: { [key: string]: number };
  readonly stackList: { [key: string]: number };
  readonly allowDupes: boolean;
  readonly stacks: StackExposures[];
};

interface ReduxProps {
  readonly selectedSlate: SlateResponse;
}

type Props = OwnProps & ReduxProps;

const QuickFavorite = (props: Props) => {
  const classes = useStyles();
  const { disabled, lineups, stacks, stackList, players, contestName, boostedPlayers, selectedSlate, playerExposureDict, allowDupes, boostedStacks } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [savedLineups, setSavedLineups] = useLocalStorage<(LateSwapLineup | GridValidRowModel)[]>(
    "lateswap-lineups",
    []
  );
  const [openModal, setOpenModal] = React.useState(false);
  const [uniquePlayersCount, setUniquePlayersCount] = React.useState<number>(0);
  const [showSkippedAlert, setShowSkippedAlert] = React.useState(false);
  const [skippedCount, setSkippedCount] = React.useState(0);

  const uniqueLimit = selectedSlate.lineupRules?.positions?.split(',').length - 1 || 8;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = async (type: string) => {
      if(type === 'roi') {
        // Clear existing lineups and start fresh
        const result = findLineupsToFavorite(lineups, players, [], playerExposureDict, stackList, boostedPlayers, [], contestName, selectedSlate, allowDupes, boostedStacks, uniquePlayersCount);
        setSavedLineups(result.lineups);

        // Show alert if lineups were skipped due to unique constraints
        if (result.skippedDueToUniques > 0) {
          setSkippedCount(result.skippedDueToUniques);
          setShowSkippedAlert(true);
        }
      } else if (type === 'proj') {
        // Clear existing lineups and start fresh
        const topProjLineups = lineups.filter((lineup) => lineup.contestName === contestName || contestName === 'all').map(lineup => {
          var topLineup = lineup?.lateSwapOptions?.sort((a,b) => b.simAverageFantasyPoints - a.simAverageFantasyPoints)[0] || {} as LateSwapLineup
          topLineup.associatedLateSwapEntries = lineup?.associatedLateSwapEntries;
          topLineup.parentRoi = lineup.simLineupRoi;
          topLineup.lineupNumber = lineup.lineupNumber;
          return topLineup;
        })

        setSavedLineups(topProjLineups);
      } else if (type === 'clear') {
        setSavedLineups([]);
      }
      setAnchorEl(null);
    };

  const handleUniqueCountChange = (number: string) => {
    const newValue = Number(number);
    if ((Number.isInteger(newValue) && !Number.isNaN(newValue) && newValue >= 0 && newValue <= uniqueLimit) || number === '') {
      setUniquePlayersCount(newValue);
    }
  };

  return (
    <div className={classes.quickFavorite}>
      <UpgradeModal open={openModal} onClose={() => setOpenModal(false)} />
      <Snackbar
        open={showSkippedAlert}
        autoHideDuration={6000}
        onClose={() => setShowSkippedAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity='warning' onClose={() => setShowSkippedAlert(false)}>
          {skippedCount} lineup{skippedCount > 1 ? 's' : ''} couldn't be favorited because of the unique constraint
        </Alert>
      </Snackbar>
      <Button aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick} disabled={disabled}>
        Quick Favorite
        <ArrowDropDownIcon />
      </Button>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        onClose={() => handleClose('')}
        open={Boolean(anchorEl)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center'}}
      >
        <MenuItem>
          <Stack direction='row' spacing={0.5} alignItems='center' columnGap='8px'>
            <Tooltip title='Number of unique players between each lineup in your favorited lineups'>
              <Stack><InfoOutlinedIcon /></Stack>
            </Tooltip>
            <Stack alignItems='center'>
              <Stack>
                Uniques:
              </Stack>
              <Stack fontSize='10px'>
                (Max {uniqueLimit})
              </Stack>
            </Stack>
            <TextField
              sx={{ width: 100 }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              size='small'
              type='number'
              value={uniquePlayersCount}
              onChange={(e) => handleUniqueCountChange(e.target.value)}
            />
          </Stack>
        </MenuItem>
        <MenuItem onClick={() => handleClose('roi')}>Favorite Top ROI Swap Lineups</MenuItem>
        {/*<MenuItem onClick={() => handleClose('proj')}>Favorite Top Avg Sim Proj Swap Lineups</MenuItem>*/}
        <MenuItem onClick={() => handleClose('clear')}>Un-Favorite All</MenuItem>
      </Menu>
    </div>
  )
}

const mapStateToProps = (state: any) => {
  const {
    selectedSlate
  } = state.common;

  return {
    selectedSlate,
  };
};

const mapDispatchToProps = {
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickFavorite);
